import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid2 as Grid,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import SubscriptionStatusChip from '../components/SubscriptionStatusChip';
import moment from 'moment';
import { Link } from 'react-router-dom';
import InvoiceStatusChip from '../components/InvoiceStausChip';
import { selectSubscription } from '../../api/auth/authSlice';
import { axiosInstance } from '../../utils/axios';
import appConfig from '../../config/appConfig';

const DashboardPage = () => {
  const user = useSelector(selectSubscription);
  const [subscription, setSubscription] = useState(null);
  const isUnlimited = subscription?.documents_limit === 0;
  const currentDocumentCount = subscription?.currentInvoice?.documents_count || 0;
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await axiosInstance.get('/users/subscription');
        setSubscription(response.data.subscription);
      } catch (error) {
        setSnackbarMessage(`Failed to fetch subscription`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    fetchSubscription();
  }, []);

  if (!subscription) {
    return (
      <>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </>
    );
  }
  const progress =
    subscription?.documents_limit > 0
      ? (currentDocumentCount / subscription?.documents_limit) * 100
      : isUnlimited
        ? 100
        : 0;
  return (
    <Box p={2}>
      <Paper elevation={0}>
        <Box sx={{ mb: '1.5rem' }}>
          <h1>Subscription Details</h1>
          <Divider></Divider>
        </Box>
        <Grid container spacing={2}>
          <Grid size={12} display={'flex'} justifyContent={'space-between'}>
            <div>
              <Typography variant="title">{subscription?.plan?.name}</Typography>{' '}
              <SubscriptionStatusChip status={subscription.status} />
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    setLoading(true);
                    const response = await axiosInstance.post('/users/create-portal-session', {
                      customer_id: subscription.stripe_customer_id,
                    });
                    window.open(response.data.session_url, '_blank');
                  } catch (error) {
                    alert('Unable to open billing portal. Please try again later.');
                  }
                }}
              >
                Manage Subscription
              </Button>
            </div>
          </Grid>
          <Grid size={2}>
            <Typography variant="subtitle1" className={'label'}>
              Current Period:
            </Typography>
          </Grid>
          <Grid size={10}>
            <Typography variant="subtitle1">
              {moment.unix(subscription.current_period_start).format('ll')} to{' '}
              {moment.unix(subscription.current_period_end).format('ll')}
            </Typography>
          </Grid>
          <Grid size={2}>
            <Typography variant="subtitle1" className={'label'}>
              Payment Buffer:
            </Typography>
          </Grid>
          <Grid size={10}>
            <Typography variant="subtitle1">{subscription.plan.due_after}</Typography>
          </Grid>
          <Grid size={2}>
            <Typography variant="subtitle1" className={'label'}>
              Started:
            </Typography>
          </Grid>
          <Grid size={10}>
            <Typography variant="subtitle1">{moment.unix(subscription.start_date).format('ll')}</Typography>
          </Grid>
          <Grid size={2}>
            <Typography variant="subtitle1" className={'label'}>
              Next Invoice:
            </Typography>
          </Grid>
          <Grid size={10}>
            <Typography variant="subtitle1">{moment.unix(subscription.current_period_end).format('ll')}</Typography>
          </Grid>
        </Grid>
        <Box sx={{ mt: '1.5rem', mb: '1.5rem' }}>
          <Typography variant="h6">Due Invoices</Typography>
          <Divider></Divider>
        </Box>
        <TableContainer component={Box}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Payment Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscription?.dueInvoices?.length ? (
                subscription.dueInvoices.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell component="th" scope="row">
                      {row.amount.toLocaleString('en-US', {
                        style: 'currency',
                        currency: appConfig.stripeCurrency,
                      })}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusChip
                        invoiceStatus={new Date() > new Date(row.due_date * 1000) ? 'Past Due' : 'Open'}
                      />
                    </TableCell>
                    <TableCell>{moment.unix(row.due_date).format('ll')}</TableCell>
                    <TableCell>{moment(row.createdAt).format('ll')}</TableCell>
                    <TableCell>
                      <Link underline="none" target="_blank" to={row.invoice_payment_url}>
                        Pay Now
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align={'center'}>
                    No due invoices
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: '1.5rem', mb: '1.5rem' }}>
          <Typography variant="h6">Usage Tracking</Typography>
          <Divider></Divider>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Typography variant="subtitle1" className={'label'}>
                Document Usage:
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography variant="subtitle1">
                {currentDocumentCount} / {isUnlimited ? '∞' : subscription?.documents_limit}
                {isUnlimited && (
                  <Typography component="span" color="primary" sx={{ ml: 1 }}>
                    (Unlimited)
                  </Typography>
                )}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, width: '40%' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: '12px',
                borderRadius: '6px',
                backgroundColor: isUnlimited ? '#f5f5f5' : '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: isUnlimited ? '#2196f3' : progress >= 90 ? '#f44336' : '#2196f3',
                  borderRadius: '6px',
                },
              }}
            />
            {isUnlimited || currentDocumentCount > 0 ? (
              <>
                {!isUnlimited && (
                  <>
                    {progress >= 90 ? (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {progress.toFixed(1)}% used
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                        {progress.toFixed(1)}% used
                      </Typography>
                    )}
                  </>
                )}
              </>
            ) : (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                0% used
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default DashboardPage;
